import React, { createContext, useEffect, useState, useRef, useReducer, useCallback, useMemo } from 'react';

import {
    createKeycloakService,
    KeycloakState,
    initKeycloak,
    isAuthenticated,
    getUserInfo,
    login,
    logout,
    loginWithCredentials,
    registerOrLogin,
    updateToken,
    keycloakReducer,
    checkEmailVerification,
    updateUserTravels,
} from '@/utils/keycloakService';

interface KeycloakContextValue {
    state: KeycloakState;
    authenticated: boolean;
    login: (options?: { redirectUri?: string; provider?: string }) => void;
    logout: () => void;
    getUserInfo: () => Promise<any>;
    loginWithCredentials: (username: string, password: string) => Promise<void>;
    registerOrLogin: (email: string, password: string) => Promise<{ registered: boolean; authenticated: boolean }>;
    updateToken: (minValidity?: number) => Promise<boolean>;
    checkEmailVerification: () => Promise<boolean>;
    updateUserTravels: (email: string) => Promise<void>;
}

const KeycloakContext = createContext<KeycloakContextValue | undefined>(undefined);

interface KeycloakProviderProps {
    children: React.ReactNode;
}

const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children }) => {
    const initRef = useRef<boolean>(false);
    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const [state, dispatch] = useReducer(keycloakReducer, null, () => {
        const keycloakConfig = {
            url: import.meta.env.VITE_KEYCLOAK_URL as string,
            realm: import.meta.env.VITE_KEYCLOAK_REALM as string,
            clientId: import.meta.env.VITE_KEYCLOAK_CLIENT as string,
            clientSecret: import.meta.env.VITE_KEYCLOAK_CLIENT_SECRET as string,
        };
        return createKeycloakService(keycloakConfig);
    });

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;

        initKeycloak(state, {
            onLoad: 'check-sso',
            silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        })
            .then((result) => {
                setAuthenticated(result.authenticated);
                dispatch({ type: 'UPDATE_STATE', payload: result.state });
            })
            .catch((error) => {
                console.error('Keycloak initialization failed:', error);
                setAuthenticated(false);
            });
    }, []);

    const contextLogin = useCallback(
        (options?: { redirectUri?: string; provider?: string }) => {
            login(state, options);
        },
        [state],
    );

    const contextLogout = useCallback(() => {
        logout(state);
    }, [state]);

    const contextGetUserInfo = useCallback(async () => {
        return getUserInfo(state);
    }, [state]);

    const contextLoginWithCredentials = useCallback(
        async (username: string, password: string) => {
            try {
                const newState = await loginWithCredentials(state, username, password);
                dispatch({ type: 'UPDATE_STATE', payload: newState });
                setAuthenticated(isAuthenticated(newState));
            } catch (error) {
                console.error('Login with credentials failed:', error);
                throw error;
            }
        },
        [state],
    );

    const contextRegisterOrLogin = useCallback(
        async (email: string, password: string) => {
            try {
                const result = await registerOrLogin(state, email, password);
                dispatch({ type: 'UPDATE_STATE', payload: result.state });
                setAuthenticated(result.authenticated);
                return { registered: result.registered, authenticated: result.authenticated };
            } catch (error) {
                console.error('Register or login failed:', error);
                throw error;
            }
        },
        [state],
    );

    const contextUpdateToken = useCallback(
        async (minValidity = 10) => {
            try {
                const result = await updateToken(state, minValidity);
                if (result.updated) {
                    dispatch({ type: 'UPDATE_STATE', payload: result.state });
                }
                return result.updated;
            } catch (error) {
                console.error('Update token failed:', error);
                return false;
            }
        },
        [state],
    );

    const contextCheckEmailVerification = useCallback(async () => {
        return checkEmailVerification(state);
    }, [state]);

    const contextUpdateUserTravels = useCallback(async (email: string) => {
        await updateUserTravels(email);
    }, []);

    const contextValue = useMemo(
        () => ({
            state,
            authenticated,
            login: contextLogin,
            logout: contextLogout,
            getUserInfo: contextGetUserInfo,
            loginWithCredentials: contextLoginWithCredentials,
            registerOrLogin: contextRegisterOrLogin,
            updateToken: contextUpdateToken,
            checkEmailVerification: contextCheckEmailVerification,
            updateUserTravels: contextUpdateUserTravels,
        }),
        [
            state,
            authenticated,
            contextLogin,
            contextLogout,
            contextGetUserInfo,
            contextLoginWithCredentials,
            contextRegisterOrLogin,
            contextUpdateToken,
            contextCheckEmailVerification,
            contextUpdateUserTravels,
        ],
    );

    return <KeycloakContext.Provider value={contextValue}>{children}</KeycloakContext.Provider>;
};

export { KeycloakProvider, KeycloakContext };
